import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injector } from '@angular/core';
import { Router } from '@angular/router';

import { AuthService } from '@auth0/auth0-angular';
import { Store } from '@ngrx/store';
import { SuiPeekPanelService } from '@semeris/ui';
import type {
  ChatConfig,
  ScratchEntryContent,
  TenantAiUsage as ChatTenantAiUsage,
  TimestampStyle,
  ToolCallInspectorData,
  ToolCallTrace,
} from '@semeris/workspace-ui/chat-config';
import { EMPTY, firstValueFrom, Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { buildDealRefHref } from './models/ref-deep-link';
import { ChatArtifactActionsService } from './services/chat-artifact-actions.service';
import { WorkspaceDocResolverService } from './services/workspace-doc-resolver.service';
import { AppActions } from '../../store/app/app.actions';
import { AppSelectors } from '../../store/app/app.selectors';
import { UserProfileSelectors } from '../../store/user-profile/user-profile.selectors';
import { ActivityEvent } from '../home/models/activity-event.model';
import { ActivityService } from '../home/services/activity.service';
import { TenantAiUsage } from '../settings/models/tenant-ai-usage.model';
import { TenantAiUsageService } from '../settings/services/tenant-ai-usage.service';
import { SemerisRoutes } from '../shared/constants/routes';
import { Permission } from '../shared/models/api/permissions.enum';
import { formatUserTimestamp } from '../shared/utils/format-user-timestamp';
import { workspaceUiApiBase } from '../workspace-ui/workspace-api-base';
import { WorkspaceImpersonationService } from '../workspace-ui/workspace-impersonation.service';

/**
 * Shown when the profile gate gives up before an Agents turn. `ChatService` catches whatever
 * `getStreamHeaders` throws and connects anyway, so this notice is the only thing that can say
 * why the turn failed; `reload_application` is the remedy, since the target stays unknowable
 * until the profile loads.
 */
const STREAM_PROFILE_UNSETTLED_NOTIFICATION = {
  message:
    'Your profile has not finished loading, so Agents cannot tell which user to act as and the message was not sent. ' +
    'Reload the application and try again.',
  action: 'Reload application',
  id: 'reload_application',
  closable: true,
  config: { color: 'warning', duration: 0, position: 'top-center', key: 'workspace-impersonation-stream-blocked' },
} as const;

/** Shape of the scratch-entry response, which nests its metadata under `descriptor`. */
interface ScratchEntryResponse {
  descriptor: { bytes: number; lines: number; origin: string };
  content: string;
}

const HTTP_FORBIDDEN = 403;

/** The chat popover shows percentages only, so the gateway's USD fields (billing-view callers) are dropped here. */
function toChatTenantUsage(usage: TenantAiUsage): ChatTenantAiUsage {
  const { fiveHour, weekly } = usage;
  return {
    id: usage.id,
    allocationConfigured: usage.allocationConfigured,
    fiveHour: { windowStart: fiveHour.windowStart, requestCount: fiveHour.requestCount, utilization: fiveHour.utilization },
    weekly: { windowStart: weekly.windowStart, requestCount: weekly.requestCount, utilization: weekly.utilization },
  };
}

/**
 * sdocs' side of the `@semeris/workspace-ui/chat` seam — transport, auth and the
 * presentation resolvers in one object.
 *
 * Auth is the app's Auth0 bearer: the SSE stream bypasses HttpClient, so it can't use
 * `aiWorkspaceAuthInterceptor` and takes the token through `getStreamHeaders` instead.
 * The agent CRUD calls do go through HttpClient and are covered by that interceptor. The
 * impersonation header follows the same split.
 *
 * Provide at ROOT, from `/chat-config` — `ChatStateManager` and `AgentApi` are
 * `providedIn: 'root'` and inject `CHAT_CONFIG`, so a per-mount binding would never be
 * seen; importing the token through `/chat` would drag the whole chat bundle eager.
 */
export function sdocsChatConfig(): ChatConfig {
  const auth = inject(AuthService);
  const http = inject(HttpClient);
  const injector = inject(Injector);
  const peekPanel = inject(SuiPeekPanelService);
  const store = inject(Store);
  const docs = inject(WorkspaceDocResolverService);
  const artifacts = inject(ChatArtifactActionsService);
  const impersonation = inject(WorkspaceImpersonationService);
  const activity = inject(ActivityService);
  const tenantUsage = inject(TenantAiUsageService);
  const router = inject(Router);
  const workspaceId = store.selectSignal(AppSelectors.selectedWorkspaceId);
  const profile = store.selectSignal(UserProfileSelectors.profile);
  const dateFormat = store.selectSignal(UserProfileSelectors.dateFormat);
  const timezone = store.selectSignal(UserProfileSelectors.timezone);
  const adminApiRoot = `${workspaceUiApiBase()}/api/admin/ai/agents`;

  const isPlatformAdmin = computed(() => profile()?.permissions?.includes(Permission.READ_DOCSTORE_ADMIN) === true);
  const tenantShortName = computed(() => profile()?.impersonatedTenant || profile()?.tenantShortName);

  // A 403 hides the tenant section for the rest of the session; other errors propagate so the package keeps its last value.
  let tenantForbiddenFor: string | undefined;
  const fetchTenantUsage = (): Observable<ChatTenantAiUsage | null> => {
    const tenant = tenantShortName();
    if (!tenant || tenant === tenantForbiddenFor) {
      return of(null);
    }
    return tenantUsage.getUsage(tenant).pipe(
      map(toChatTenantUsage),
      catchError((error: unknown) => {
        if (error instanceof HttpErrorResponse && error.status === HTTP_FORBIDDEN) {
          tenantForbiddenFor = tenant;
          return of(null);
        }
        return throwError(() => error);
      }),
    );
  };

  const fetchToolCallTrace = async (workspace: string, agentId: string, toolCallId: string): Promise<ToolCallTrace | null> => {
    const trace = await firstValueFrom(
      http.get<ToolCallTrace & { available: boolean }>(
        `${adminApiRoot}/${workspace}/${agentId}/tool-calls/${encodeURIComponent(toolCallId)}`,
      ),
    );
    return trace.available ? trace : null;
  };

  const fetchScratchEntry = async (workspace: string, agentId: string, name: string): Promise<ScratchEntryContent | null> => {
    try {
      const entry = await firstValueFrom(
        http.get<ScratchEntryResponse>(`${adminApiRoot}/${workspace}/${agentId}/scratch/${encodeURIComponent(name)}`),
      );
      return { content: entry.content, ...entry.descriptor };
    } catch (error) {
      // A swept or rewritten-away entry is an expected state, not an error.
      if (error instanceof HttpErrorResponse && error.status === 404) {
        return null;
      }
      throw error;
    }
  };

  return {
    apiUrlBuilder: (id) => `${workspaceUiApiBase()}/api/workspaces/${id}/ai`,
    // The stream bypasses HttpClient, so `workspaceImpersonateInterceptor` cannot reach it —
    // including its wait for the profile, without which `header()` cannot tell "not
    // impersonating" from "not loaded yet".
    //
    // The gate is handled here rather than thrown: `ChatService` catches a rejection from this
    // hook and connects with `extraHeaders = {}` (`chat/src/services/chat.service.ts:606`, a
    // synced copy that is not ours to edit), so throwing cannot stop the stream. Returning no
    // headers is what keeps a never-settling profile from running the turn as the admin — this
    // hook is the stream's only source of a bearer, which is why `getAuthToken` is deliberately
    // not part of this config; add one and the turn would connect as the admin with no
    // impersonation header. The turn then fails on the gateway's 401, and the notice explains it.
    getStreamHeaders: async () => {
      try {
        await firstValueFrom(impersonation.whenResolved());
      } catch {
        store.dispatch(AppActions.PushNotification({ ...STREAM_PROFILE_UNSETTLED_NOTIFICATION }));
        return {};
      }
      return {
        ...impersonation.header(),
        Authorization: `Bearer ${await firstValueFrom(auth.getAccessTokenSilently())}`,
      };
    },
    resolveArtifactState: artifacts.chatResolvers.resolveArtifactState,
    isArtifactBusy: artifacts.chatResolvers.isArtifactBusy,
    resolveDocRef: (docId) => docs.loadDocumentMeta(docId, workspaceId()),
    resolveLibraryName: (libraryId) => docs.libraryName(libraryId, workspaceId()) ?? '',
    buildRefHref: (ref) => buildDealRefHref(ref),
    onMessageSent: (info) =>
      activity
        .addActivity(ActivityEvent.WorkspaceAgentMessageSend, info)
        .pipe(catchError(() => EMPTY))
        .subscribe(),
    fetchTenantUsage,
    openUsageSettings: () => void router.navigate(['/', SemerisRoutes.settings.root, SemerisRoutes.settings.profile]),

    // Signals read per call, never captured: the profile lands after this config is built.
    formatTimestamp: (iso: string, style: TimestampStyle) => formatUserTimestamp(iso, style, dateFormat(), timezone()),
    timeZone: timezone,

    // Presence is what renders the progress card's inspector icon, so both adapters read as
    // `undefined` for non-admins — the gateway gates the routes regardless. Getters, not plain
    // properties: `CHAT_CONFIG` is built once at root, before the profile has loaded, so a value
    // fixed here would deny every admin. Read this way the package's own `computed` tracks the
    // signal and the icon appears as soon as permissions land.
    get fetchToolCallTrace() {
      return isPlatformAdmin() ? fetchToolCallTrace : undefined;
    },
    get fetchScratchEntry() {
      return isPlatformAdmin() ? fetchScratchEntry : undefined;
    },

    // Where an inspection opens: the assistant screen docks it as an artifact tab, `ChatView`
    // having claimed the host on init via `setToolInspectorHost`. Anywhere else — deal-details
    // embeds `<zing-chat>` with no `ChatView` — nothing claims it, and the peek panel is the
    // fallback. Configuring this adapter at all suppresses the package's own peek-panel
    // fallback, hence the boolean: without it the icon is visibly dead outside the assistant.
    //
    // `/chat` is imported at click time, not named here: naming it would pull the whole chat
    // barrel into the initial chunk, and an eager `inject` is an NG0200 cycle (the chat services
    // inject `CHAT_CONFIG` back). By the time this runs the click has loaded `/chat` anyway.
    openToolInspector: async (data: ToolCallInspectorData) => {
      const { ChatNavigationService, ToolCallInspector } = await import('@semeris/workspace-ui/chat');
      if (injector.get(ChatNavigationService).openToolInspector(data)) {
        return;
      }
      peekPanel.open(ToolCallInspector, { data, width: 'xl' });
    },
  };
}
